import {useQuery, useLazyQuery} from '@apollo/client';
import {useCallback} from 'react';
import {
    GET_PARTNER_PENALTY_LIST,
    GET_PARTNER_PENALTY_STATUS_COUNTS,
    GET_WAREHOUSE_PENALTY_LIST,
    GET_WAREHOUSE_PENALTY_STATUS_COUNTS,
    GetPartnerPenaltyListResponse,
    GetPartnerPenaltyListVariables,
    GetPartnerPenaltyStatusCountsResponse,
    GetPartnerPenaltyStatusCountsVariables,
} from '../graphql/queries';
import {
    PartnerPenaltyFiltersTypeInput,
    PartnerPenaltyItem,
    PartnerPenaltyStatus,
    PartnerPenaltyStatusCounts,
    PenaltyCenterMode,
} from '../graphql/types';

/**
 * Page 层组装好的筛选输入（不含 statuses，由 hook 按当前 Tab 补齐）。
 * warehouse 模式传入的是 WarehousePenaltyFiltersTypeInput，多出的 warehouseId / dspId
 * 随 spread 原样进入 variables —— 两种模式共用这一个类型即可。
 */
export type PenaltySharedFilters = Omit<PartnerPenaltyFiltersTypeInput, 'statuses'>;

/**
 * 罚单列表查询。
 * statuses 恒为当前 Tab 的单值数组，绝不传空 —— 否则 TIMEOUT 行会漏进列表。
 * mode 只决定用哪个 document，warehouse 版靠 GraphQL alias 返回同样的响应 key。
 */
export const usePenaltyCenterList = (mode: PenaltyCenterMode = 'dsp') => {
    const [execute, {data, loading, error}] = useLazyQuery<
        GetPartnerPenaltyListResponse,
        GetPartnerPenaltyListVariables
    >(mode === 'warehouse' ? GET_WAREHOUSE_PENALTY_LIST : GET_PARTNER_PENALTY_LIST, {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
    });

    const executeQuery = useCallback(
        (
            sharedFilters: PenaltySharedFilters,
            status: PartnerPenaltyStatus,
            page: number,
            size: number
        ) => {
            execute({
                variables: {
                    filters: {...sharedFilters, statuses: [status]},
                    page,
                    size,
                },
            });
        },
        [execute]
    );

    return {
        data: data?.partnerPenaltyPageList?.content ?? ([] as PartnerPenaltyItem[]),
        totalElements: data?.partnerPenaltyPageList?.totalElements ?? 0,
        loading,
        error,
        executeQuery,
    };
};

const EMPTY_COUNTS: PartnerPenaltyStatusCounts = {
    pendingSubmit: 0,
    reviewing: 0,
    approved: 0,
    rejected: 0,
    waiting: 0,
    timeout: 0,
    accepted: 0,
};

/** 全部 Tab 的计数一次查询拿齐（对比 reviewTask 的逐 Tab 独立查询省多次 RPC） */
export const usePenaltyCenterTabCounts = (
    sharedFilters: PenaltySharedFilters,
    mode: PenaltyCenterMode = 'dsp',
    /** warehouse 模式下仓库未选定时置 true，避免发出 warehouseId=0 的无效查询 */
    skip = false
) => {
    const {data, loading, refetch} = useQuery<
        GetPartnerPenaltyStatusCountsResponse,
        GetPartnerPenaltyStatusCountsVariables
    >(mode === 'warehouse' ? GET_WAREHOUSE_PENALTY_STATUS_COUNTS : GET_PARTNER_PENALTY_STATUS_COUNTS, {
        variables: {filters: sharedFilters},
        skip,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });

    const counts = data?.partnerPenaltyStatusCounts ?? EMPTY_COUNTS;

    const getCount = useCallback(
        (status: PartnerPenaltyStatus): number => {
            switch (status) {
                case PartnerPenaltyStatus.PENDING_SUBMIT:
                    return counts.pendingSubmit;
                case PartnerPenaltyStatus.REVIEWING:
                    return counts.reviewing;
                case PartnerPenaltyStatus.APPROVED:
                    return counts.approved;
                case PartnerPenaltyStatus.REJECTED:
                    return counts.rejected;
                case PartnerPenaltyStatus.WAITING:
                    return counts.waiting ?? 0;
                case PartnerPenaltyStatus.TIMEOUT:
                    return counts.timeout ?? 0;
                case PartnerPenaltyStatus.ACCEPTED:
                    return counts.accepted ?? 0;
                default:
                    return 0;
            }
        },
        [counts]
    );

    return {getCount, loading, refetchCounts: refetch};
};
